import { AspectRatio, aspectRatios } from '@/app/ai-expand/processing/config';
import { Coordinates } from '@dnd-kit/utilities';
import { StateCreator, StoreApi } from 'zustand';
import type { Position } from '@/app/ai-expand/processing/components/ResizeHandle';

const initialState = {
  // 左侧工具栏是否折叠
  isFold: false,
  isExpanding: false,
  activeHandle: null as Position | null,
  selectedAspectRatio: aspectRatios[0],
  zoom: 100,
  draggableSize: { width: 0, height: 0 },
  imageSize: { width: 0, height: 0 },
  coordinates: { x: 0, y: 0 },
  selectionSize: { width: 0, height: 0 },
  contentPosition: { x: 0, y: 0 },
  borderPositions: { top: 0, right: 0, bottom: 0, left: 0 },
  aiExpandProgress: {
    progress: 0,
    timeLeft: 0,
  },
  imgInfo: {
    hasLoaded: false,
    isUploading: false,
    filename: '',
    // 图片原始尺寸，上传完图片或者扩完图片会进行更新
    naturalWidth: 0,
    naturalHeight: 0,
    // 图片一开始的显示尺寸，上传完图片会进行更新
    originalWidth: 0,
    originalHeight: 0,
    // 图片一开始的原始尺寸，上传完图片会进行更新
    originalNaturalWidth: 0,
    originalNaturalHeight: 0,
    blobUrl: '',
    original_access_url: '',
    original_file_id: '',
    current_access_url: '',
    current_file_id: '',
    expand_access_url: '',
    expand_file_id: '',
    expand_thumbnail_url: '',
    preview_access_url: '',
  },
};

export type BorderPosition = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

type AiExpandProgress = {
  progress: number;
  timeLeft: number;
};

type ImgInfo = {
  hasLoaded: boolean;
  isUploading: boolean;
  filename: string;
  naturalWidth: number;
  naturalHeight: number;
  originalWidth: number;
  originalHeight: number;
  originalNaturalWidth: number;
  originalNaturalHeight: number;
  blobUrl: string;
  original_access_url: string;
  original_file_id: string;
  current_access_url: string;
  current_file_id: string;
  expand_access_url: string;
  expand_file_id: string;
  expand_thumbnail_url: string;
  preview_access_url: string;
};

type AiExpandState = typeof initialState;

type AiExpandAction = {
  setIsFold: (value: boolean) => void;
  setIsExpanding: (value: boolean) => void;
  setActiveHandle: (value: Position | null) => void;
  setSelectedAspectRatio: (value: AspectRatio) => void;
  setZoom: (value: number) => void;
  setDraggableSize: (value: Size) => void;
  setImageSize: (value: Size) => void;
  setCoordinates: (
    value: Coordinates | ((prev: Coordinates) => Coordinates)
  ) => void;
  setSelectionSize: (value: Size | ((prev: Size) => Size)) => void;
  setContentPosition: (
    value: Coordinates | ((prev: Coordinates) => Coordinates)
  ) => void;
  setBorderPositions: (
    value: BorderPosition | ((prev: BorderPosition) => BorderPosition)
  ) => void;
  setImgInfo: (value: ImgInfo | ((prev: ImgInfo) => ImgInfo)) => void;
  setAiExpandProgress: (
    value: AiExpandProgress | ((prev: AiExpandProgress) => AiExpandProgress)
  ) => void;
};

export type AiExpandStore = AiExpandState & AiExpandAction;

type Size = { width: number; height: number };

const aiExpandActions = (
  set: StoreApi<AiExpandStore>['setState'],
  get: StoreApi<AiExpandStore>['getState'],
  api: StoreApi<AiExpandStore>
) => ({
  setIsFold: (value: boolean) => set({ isFold: value }),
  setIsExpanding: (value: boolean) => set({ isExpanding: value }),
  setActiveHandle: (value: Position | null) => set({ activeHandle: value }),
  setSelectedAspectRatio: (value: AspectRatio) =>
    set({ selectedAspectRatio: value }),
  setZoom: (value: number) => set({ zoom: value }),
  setDraggableSize: (value: Size) => set({ draggableSize: value }),
  setImageSize: (value: Size) => set({ imageSize: value }),
  setCoordinates: (value: Coordinates | ((prev: Coordinates) => Coordinates)) =>
    set({
      coordinates:
        typeof value === 'function' ? value(get().coordinates) : value,
    }),
  setSelectionSize: (value: Size | ((prev: Size) => Size)) =>
    set({
      selectionSize:
        typeof value === 'function' ? value(get().selectionSize) : value,
    }),
  setContentPosition: (
    value: Coordinates | ((prev: Coordinates) => Coordinates)
  ) =>
    set({
      contentPosition:
        typeof value === 'function' ? value(get().contentPosition) : value,
    }),
  setBorderPositions: (
    value: BorderPosition | ((prev: BorderPosition) => BorderPosition)
  ) =>
    set({
      borderPositions:
        typeof value === 'function' ? value(get().borderPositions) : value,
    }),
  setImgInfo: (value: ImgInfo | ((prev: ImgInfo) => ImgInfo)) =>
    set({
      imgInfo: typeof value === 'function' ? value(get().imgInfo) : value,
    }),
  setAiExpandProgress: (
    value: AiExpandProgress | ((prev: AiExpandProgress) => AiExpandProgress)
  ) =>
    set({
      aiExpandProgress:
        typeof value === 'function' ? value(get().aiExpandProgress) : value,
    }),
});

export const useAiExpandStore: StateCreator<AiExpandStore> = (
  set,
  get,
  api
) => ({
  ...initialState,
  ...aiExpandActions(set, get, api),
});
